import CloseIcon from "@mui/icons-material/Close";
import { Button, IconButton } from "@mui/material";
import { useEffect, useRef } from "react";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import {
	useCancelPrint,
	usePrintQueueItem,
	useReprint,
} from "../hooks/usePrinters";

type Severity = "error" | "info" | "success" | "warning";
const PROGRESS: Record<string, string> = {
	Processing: "Sending…",
	Sent: "Printing…",
};

type Props = {
	printQueueItemId: number;
	onClose: () => void;
	onError: (e: Error) => void;
	onItemChange?: (printQueueItemId: number) => void;
};

export const PrintToast = ({
	printQueueItemId,
	onClose,
	onError,
	onItemChange,
}: Props) => {
	const { data, unconfirmed } = usePrintQueueItem(printQueueItemId, onError);
	const item = data?.item;
	const status = item?.Status;
	const cancel = useCancelPrint(onError);
	const reprint = useReprint((id) => onItemChange?.(id), onError);

	const failed = status === "Failed" || status === "Expired";
	const reported = useRef<number | null>(null);
	useEffect(() => {
		if (failed && reported.current !== printQueueItemId) {
			reported.current = printQueueItemId;
			onError(new Error(item?.Message || `Print ${status?.toLowerCase()}`));
		}
	}, [failed, status, printQueueItemId, item?.Message, onError]);

	const waiting = status === "Pending" && !!item?.Message;
	const [severity, message]: [Severity, string] = failed
		? ["error", item?.Message || `Print ${status?.toLowerCase()}`]
		: status === "Printed"
			? ["success", "Tag printed"]
			: status === "Cancelled"
				? ["info", "Print cancelled"]
				: status === "Sent" && unconfirmed
					? ["warning", "Sent, the printer did not confirm it"]
					: waiting
						? ["warning", `Waiting: ${item?.Message}`]
						: ["info", PROGRESS[status ?? ""] ?? "Queued…"];

	const button = waiting
		? {
				label: "Cancel",
				busy: cancel.isLoading,
				run: () => cancel.mutate(printQueueItemId),
			}
		: failed
			? {
					label: "Reprint",
					busy: reprint.isLoading,
					run: () => reprint.mutate(printQueueItemId),
				}
			: null;
	// an Alert action replaces its close button
	const action = button && (
		<>
			<Button
				color="inherit"
				size="small"
				disabled={button.busy}
				onClick={button.run}
			>
				{button.label}
			</Button>
			<IconButton color="inherit" size="small" onClick={onClose}>
				<CloseIcon fontSize="small" />
			</IconButton>
		</>
	);

	return (
		<NotificationSnackBar
			message={message}
			severity={severity}
			onHide={onClose}
			action={action ?? undefined}
			autoHideDuration={
				status === "Printed" || status === "Cancelled" ? 2500 : null
			}
		/>
	);
};
